import { track } from "../../lib/analytics.js";

// Single source of truth for the pace model's defaults — reused as the
// store's initial value below, and as the fallback in components that read
// app.paceSettings before the store has hydrated (e.g. isolated tests).
export const DEFAULT_PACE_SETTINGS = {
  basePaceSPerKm: 500, // 8:20/km — ultra-trail default (Moderate preset)
  kFatigue: 0.002, // cumulative fatigue coefficient (Moderate preset, exponential model)
  lifeBaseStopS: 3600, // planned stop at each LifeBase in seconds (1 hour default)
};

export const createAppSlice = (set, get) => {
  return {
    app: {
      trackingMode: false,
      displaySlopes: false,
      profileMode: false,
      locations: [],
      pendingUrl: null,
      liveSessionId: null,
      liveWriteKey: null,
      mode: null,
      followerRoomId: null,
      raceId: null,
      currentRoute: "/",
      installPromptDismissed: false,
      highlightedClimbIndex: null,
      paceSettings: DEFAULT_PACE_SETTINGS,
      theme:
        typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-color-scheme: dark)").matches
          ? "dark"
          : "light",
      outdoorMode: false,
    },

    toggleTrackingMode: () =>
      set(
        (state) => ({
          app: {
            ...state.app,
            trackingMode: !state.app.trackingMode,
          },
        }),
        undefined,
        "app/toggleTrackingMode",
      ),

    toggleProfileMode: () => {
      track("toggle-profile");
      set(
        (state) => ({
          app: {
            ...state.app,
            profileMode: !state.app.profileMode,
          },
        }),
        undefined,
        "app/toggleProfileMode",
      );
    },
    toggleSlopesMode: () => {
      track("toggle-slopes");
      set(
        (state) => ({
          app: {
            ...state.app,
            displaySlopes: !state.app.displaySlopes,
          },
        }),
        undefined,
        "app/toggleSlopesMode",
      );
    },

    setPendingUrl: (url) =>
      set(
        (state) => ({ app: { ...state.app, pendingUrl: url } }),
        undefined,
        "app/setPendingUrl",
      ),

    setLiveSessionId: (id) =>
      set(
        (state) => ({ app: { ...state.app, liveSessionId: id } }),
        undefined,
        "app/setLiveSessionId",
      ),

    setLiveWriteKey: (key) =>
      set(
        (state) => ({ app: { ...state.app, liveWriteKey: key } }),
        undefined,
        "app/setLiveWriteKey",
      ),

    setMode: (mode) => {
      set(
        (state) => ({ app: { ...state.app, mode } }),
        undefined,
        "app/setMode",
      );
      // Clear follower room when returning to the wizard so the app
      // doesn't auto-reconnect to the previous session on next cold start
      if (mode === null) {
        get().setFollowerRoomId(null);
      }
    },

    setFollowerRoomId: (id) =>
      set(
        (state) => ({ app: { ...state.app, followerRoomId: id } }),
        undefined,
        "app/setFollowerRoomId",
      ),

    setRaceId: (id) =>
      set(
        (state) => ({ app: { ...state.app, raceId: id } }),
        undefined,
        "app/setRaceId",
      ),

    setCurrentRoute: (route) =>
      set(
        (state) => ({ app: { ...state.app, currentRoute: route } }),
        undefined,
        "app/setCurrentRoute",
      ),

    toggleTheme: () => {
      track("toggle-theme");
      set(
        (state) => ({
          app: {
            ...state.app,
            theme: state.app.theme === "dark" ? "light" : "dark",
          },
        }),
        undefined,
        "app/toggleTheme",
      );
    },

    toggleOutdoorMode: () => {
      track("toggle-outdoor-mode");
      set(
        (state) => ({
          app: {
            ...state.app,
            outdoorMode: !state.app.outdoorMode,
          },
        }),
        undefined,
        "app/toggleOutdoorMode",
      );
    },

    dismissInstallPrompt: () =>
      set(
        (state) => ({ app: { ...state.app, installPromptDismissed: true } }),
        undefined,
        "app/dismissInstallPrompt",
      ),

    setHighlightedClimb: (index) =>
      set(
        (state) => ({ app: { ...state.app, highlightedClimbIndex: index } }),
        undefined,
        "app/setHighlightedClimb",
      ),

    setPaceSettings: ({ basePaceSPerKm, kFatigue, lifeBaseStopS }) => {
      set(
        (state) => ({
          app: {
            ...state.app,
            paceSettings: {
              basePaceSPerKm:
                basePaceSPerKm ?? state.app.paceSettings.basePaceSPerKm,
              kFatigue: kFatigue ?? state.app.paceSettings.kFatigue,
              lifeBaseStopS:
                lifeBaseStopS ?? state.app.paceSettings.lifeBaseStopS,
            },
          },
        }),
        undefined,
        "app/setPaceSettings",
      );

      // The cached Zig recalibration was computed under the previous pace
      // settings and is keyed only by trace index, so recalLookup() would
      // keep preferring it over the freshly reprocessed a-priori ETAs
      // (both callers of setPaceSettings immediately call reprocessGPXFile)
      // until the next GPS fix. Clear it so checkpoint/stage ETAs reflect
      // the new pace right away instead of showing stale numbers until then.
      get().clearRecalibration?.();
    },
  };
};
